// Talks to the STORE OS server. Every request carries the x-sos header the server requires.
import { demoKind } from './demoKind';

export class ApiError extends Error {
  status: number;
  code: string;
  details: any;
  constructor(status: number, message: string, code: string, details: any) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(fn: Listener) {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

type SavedListener = (path: string) => void;
const savedListeners = new Set<SavedListener>();
/** Called after a request that changed something went through (any POST, PATCH or DELETE), with its path. The goals bar refreshes on it. */
export function onSaved(fn: SavedListener) {
  savedListeners.add(fn);
  return () => savedListeners.delete(fn);
}

export async function api<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  // The public demo: which sample shop this tab shows (the server ignores it everywhere else).
  const kind = demoKind();
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', 'x-sos': '1', ...(kind ? { 'x-sos-demo-kind': kind } : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'No connection to STORE OS. Check the internet and try again.', 'offline', null);
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = data?.error ?? {};
    if (res.status === 401 && err.code === 'unauthorized') unauthorizedListeners.forEach((fn) => fn());
    throw new ApiError(res.status, err.message ?? 'Something went wrong.', err.code ?? 'error', err.details ?? null);
  }
  if (method !== 'GET') savedListeners.forEach((fn) => fn(path.split('?')[0]));
  return data as T;
}

export const get = <T = any>(p: string) => api<T>('GET', p);
export const post = <T = any>(p: string, b?: unknown) => api<T>('POST', p, b ?? {});
export const patch = <T = any>(p: string, b?: unknown) => api<T>('PATCH', p, b ?? {});
export const del = <T = any>(p: string) => api<T>('DELETE', p);

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}
